import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, AlertTriangle } from 'lucide-react';
import SecureDocumentManager from '../components/organisms/SecureDocumentManager';
import Card from '../components/atoms/Card';
import { useAuthStore } from '../store/authStore';

const SecureDocumentsPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication Required
          </h1>
          <p className="text-gray-600 mb-6">
            You must be logged in to access the secure document management system.
          </p>
          <a 
            href="/auth" 
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Sign In
          </a>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Shield className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  Secure Document Management
                </h1>
                <p className="text-gray-600">
                  End-to-end encrypted storage with strict access control
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex items-center bg-primary-50 text-primary-700 px-3 py-1 rounded-full">
                <Lock className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Secure Mode</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div className="ml-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Security Information
                </h2>
                <p className="text-gray-600 mt-1">
                  All documents are encrypted with AES-256 before being uploaded. Only you and users you explicitly share with can access your documents.
                </p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <Lock className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-700">End-to-End Encryption</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-700">Granular Permissions</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                      <Activity className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm text-gray-700">Complete Audit Logging</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <SecureDocumentManager />
        </motion.div>
      </main>
    </div>
  );
};

// Import the Users and Activity components from lucide-react
import { Users, Activity } from 'lucide-react';

export default SecureDocumentsPage;